import {lazy} from 'react';
import {Navigate, Outlet} from 'react-router-dom';
import {Layout} from './components/Layout/Layout';
import Login from './components/Login/Login';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AdminRoute from './components/ProtectedRoute/AdminRoute';
import NurseOrAdminRoute from './components/ProtectedRoute/NurseOrAdminRoute';
import ContentOrAdminRoute from './components/ProtectedRoute/ContentOrAdminRoute';
import AdminOrDeveloperRoute from './components/ProtectedRoute/AdminOrDeveloperRoute';
import AdminOrOperationsRoute from './components/ProtectedRoute/AdminOrOperationsRoute';
import CareTeamRoute from './components/ProtectedRoute/CareTeamRoute';
import RoleRoute from './components/ProtectedRoute/RoleRoute';
import SalesOnlyRedirect from './components/ProtectedRoute/SalesOnlyRedirect';
import PortalAccessGate from './components/ProtectedRoute/PortalAccessGate';
import { Role } from './generated/graphql';
import { STONE_MODE_READ_ROLES } from './features/stoneModes/access';
import { CRM_ROLES } from './utils/routeRoles';
import TestAuth from './pages/TestAuth';
import CrmIndexRedirect from './pages/CRM/CrmIndexRedirect';
import NotFound from './pages/NotFound/NotFound';

const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const ChatInbox = lazy(() => import('./pages/Dashboard/ChatInbox'));
const RecentActivity = lazy(() => import('./pages/Dashboard/RecentActivity'));
const RecentFeedback = lazy(() => import('./pages/Dashboard/RecentFeedback'));
const CompanyKpis = lazy(() => import('./pages/Dashboard/CompanyKpis'));
const Analytics = lazy(() => import('./pages/Analytics/Analytics'));
const ChatAnalytics = lazy(() => import('./pages/Dashboard/ChatAnalytics'));
const HealthAnalytics = lazy(() => import('./pages/Dashboard/HealthAnalytics'));
const SleepAnalytics = lazy(() => import('./pages/Dashboard/SleepAnalytics'));
const ProductAnalytics = lazy(() => import('./pages/Analytics/ProductAnalytics'));
const ContentAnalytics = lazy(() => import('./pages/Analytics/ContentAnalytics'));
// YOJO-2876 [Habituation section in Care Portal analytics] slice 3/6: cohort view.
// Deliberately NOT wrapped in NurseOrAdminRoute — the cohort roll-up is safe by
// shape (no identifiers) and open to analysts too (overview decision 3); the
// page skip-gates the identified members query to nurse/admin itself.
const Habituation = lazy(() => import('./pages/Analytics/Habituation/Habituation'));
// YOJO-2883 [Habituation 4/6 — per-member drill-down]: identified per-member view,
// gated to nurse/admin (the backend habituationMember @auth throws for analysts).
const HabituationMember = lazy(() => import('./pages/Analytics/Habituation/HabituationMember'));
const Cohorts = lazy(() => import('./pages/Cohorts'));
const Users = lazy(() => import('./pages/Users/Users'));
const Participants = lazy(() => import('./pages/Users/Participants'));
const Consults = lazy(() => import('./pages/Users/Consults'));
const Organizations = lazy(() => import('./pages/Users/Organizations'));
const YojoTeam = lazy(() => import('./pages/Users/YojoTeam'));
const BulkActivities = lazy(() => import('./pages/Users/BulkActivities'));
const BulkActivitiesNew = lazy(() => import('./pages/Users/BulkActivitiesNew'));
const BulkActivityDetail = lazy(() => import('./pages/Users/BulkActivityDetail'));
// YOJO-1766: the v1 WellbeingHub components stay on disk as the redirect *source*
// (reversible by design), but are no longer mounted as routes. Every legacy
// /users/:userId/wellbeing/* path renders WellbeingRedirect -> /participant-hub/*.
const WellbeingRedirect = lazy(() => import('./pages/ParticipantHub/WellbeingRedirect'));
const ParticipantHub = lazy(() => import('./pages/ParticipantHub/ParticipantHub'));
const OverviewTab = lazy(() => import('./pages/ParticipantHub/components/OverviewTab'));
const HealthDataTab = lazy(() => import('./pages/ParticipantHub/components/HealthDataTab'));
const SleepTab = lazy(() => import('./pages/ParticipantHub/components/SleepTab'));
const CarePlanTab = lazy(() => import('./pages/ParticipantHub/components/CarePlanTab'));
const ConsultsTab = lazy(() => import('./pages/ParticipantHub/components/ConsultsTab'));
const ProfileHistoryTab = lazy(() => import('./pages/ParticipantHub/components/ProfileHistoryTab'));
const StoneModeTab = lazy(() => import('./pages/ParticipantHub/components/StoneModeTab'));
const Programs = lazy(() => import('./pages/Programs/Programs'));
const LearningPaths = lazy(() => import('./pages/Programs/LearningPaths.tsx'));
const LearningJourneys = lazy(() => import('./pages/Programs/LearningJourneys.tsx'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Tools = lazy(() => import('./pages/Tools/Tools'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const PersonalInfo = lazy(() => import('./pages/Profile/PersonalInfo'));
const ApiKeys = lazy(() => import('./pages/Profile/ApiKeys'));
const PermissionGraph = lazy(() => import('./pages/Settings/PermissionGraph'));
const PushNotifications = lazy(() => import('./pages/Settings/PushNotifications/PushNotifications'));
const DeadLetterQueue = lazy(() => import('./pages/Settings/DeadLetterQueue'));
const LogbackConfig = lazy(() => import('./pages/Settings/LogbackConfig/LogbackConfig'));
const Partners = lazy(() => import('./pages/Settings/Partners'));
const ServiceApiKeys = lazy(() => import('./pages/Settings/ServiceApiKeys'));
const Capabilities = lazy(() => import('./pages/Settings/Capabilities'));
const MobileVersions = lazy(() => import('./pages/Settings/MobileVersions/MobileVersions'));
const MobileAdoption = lazy(() => import('./pages/Settings/MobileVersions/MobileAdoption'));
const MobileVersionsList = lazy(() => import('./pages/Settings/MobileVersions/MobileVersionsList'));
const MobileCachePolicies = lazy(() => import('./pages/Settings/MobileVersions/MobileCachePolicies'));
const Firmware = lazy(() => import('./pages/Settings/Firmware/Firmware'));
const FirmwareReleases = lazy(() => import('./pages/Settings/Firmware/FirmwareReleases'));
const FirmwareAdoption = lazy(() => import('./pages/Settings/Firmware/FirmwareAdoption'));
const FirmwareDevices = lazy(() => import('./pages/Settings/Firmware/FirmwareDevices'));
const FirmwareCohorts = lazy(() => import('./pages/Settings/Firmware/FirmwareCohorts'));
const Replay = lazy(() => import('./pages/Settings/Replay/Replay'));
const ContentSync = lazy(() => import('./pages/Tools/ContentSync/ContentSync'));
const StoneModes = lazy(() => import('./pages/Tools/StoneModes/StoneModes'));
const StoneModeDetailPage = lazy(() => import('./pages/Tools/StoneModes/StoneModeDetailPage'));
const StripeCheckoutTest = lazy(() => import('./pages/Settings/StripeCheckoutTest'));
const UserLookup = lazy(() => import('./pages/Settings/UserLookup/UserLookup'));
const CRM = lazy(() => import('./pages/CRM/CRM'));
const CRMDashboard = lazy(() => import('./pages/CRM/Dashboard'));
const Customers = lazy(() => import('./pages/CRM/Customers'));
const CRMShipments = lazy(() => import('./pages/CRM/Shipments'));
const CRMSources = lazy(() => import('./pages/CRM/Sources'));
const CRMCarriers = lazy(() => import('./pages/CRM/Carriers'));
const CRMServiceCases = lazy(() => import('./pages/CRM/ServiceCases'));
const CRMWaitlist = lazy(() => import('./pages/CRM/Waitlist'));
const CRMPartnerEnquiries = lazy(() => import('./pages/CRM/PartnerEnquiries'));
const CRMNewsletter = lazy(() => import('./pages/CRM/Newsletter'));
const Reports = lazy(() => import('./pages/CRM/Reports'));
const CustomerDetail = lazy(() => import('./pages/CRM/CustomerDetail'));
const Operations = lazy(() => import('./pages/Operations/Operations'));
const Segments = lazy(() => import('./pages/Operations/Segments'));
const MemberGrants = lazy(() => import('./pages/Operations/MemberGrants'));
const StoneDeviceSettings = lazy(() => import('./pages/Operations/StoneDeviceSettings'));
const Broadcasts = lazy(() => import('./pages/Users/Broadcasts'));
const BroadcastsNew = lazy(() => import('./pages/Users/BroadcastsNew'));
const BroadcastDetail = lazy(() => import('./pages/Users/BroadcastDetail'));
const PaymentLinks = lazy(() => import('./pages/PaymentLinks/PaymentLinks'));

export const routes = [
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />
  },
  {
    path: '/login',
    element: <Login />
  },
  {
    path: '/test-auth',
    element: <TestAuth />
  },
  {
    path: '/',
    // YOJO-3614 [growth staff role + growth MCP door]: nothing in the portal mounts until the
    // user's roles are known (spinner; a failed roles query fails closed with a Retry), and a
    // growth-only user opens their own profile and no other page (anything else sends them there).
    element: <ProtectedRoute><PortalAccessGate><Layout /></PortalAccessGate></ProtectedRoute>,
    children: [
      {
        // YOJO-3371 [care-portal CRM page for partner enquiries]: a sales-only user has no
        // dashboard; they are sent on to /crm/partner-enquiries (no toast). Other roles untouched.
        path: 'dashboard',
        element: <SalesOnlyRedirect><Dashboard /></SalesOnlyRedirect>,
        children: [
          { path: 'chat', element: <ChatInbox /> },
          { path: 'feedback', element: <RecentFeedback /> },
          { path: 'activity', element: <RecentActivity /> },
          { path: 'kpis', element: <CompanyKpis /> }
        ]
      },
      {
        path: 'analytics',
        element: <Analytics />,
        children: [
          { path: 'chat', element: <ChatAnalytics /> },
          { path: 'health', element: <HealthAnalytics /> },
          { path: 'sleep', element: <SleepAnalytics /> },
          { path: 'product', element: <ProductAnalytics /> },
          { path: 'content', element: <ContentAnalytics /> },
          { path: 'cohorts', element: <NurseOrAdminRoute><Cohorts /></NurseOrAdminRoute> },
          { path: 'habituation', element: <Habituation /> },
          { path: 'habituation/:memberId', element: <NurseOrAdminRoute><HabituationMember /></NurseOrAdminRoute> }
        ]
      },
      {
        path: 'users',
        element: <Users />,
        children: [
          { index: true, element: <Navigate to="/users/participants" replace /> },
          { path: 'participants', element: <NurseOrAdminRoute><Participants /></NurseOrAdminRoute> },
          // YOJO-2081 [cross-participant consult roster + "needs a consult" filter]:
          // care-team-only roster of every participant's consult status. Gated to
          // nurse/admin like Participants (matches the server-side
          // `@auth(roles: ["nurse"])` on the consultRoster query).
          { path: 'consults', element: <NurseOrAdminRoute><Consults /></NurseOrAdminRoute> },
          { path: 'bulk-activities', element: <NurseOrAdminRoute><BulkActivities /></NurseOrAdminRoute> },
          { path: 'bulk-activities/new', element: <NurseOrAdminRoute><BulkActivitiesNew /></NurseOrAdminRoute> },
          { path: 'bulk-activities/:bulkAssignmentId', element: <NurseOrAdminRoute><BulkActivityDetail /></NurseOrAdminRoute> },
          // Broadcast pages — care-team-only (nurse OR admin). Relocated from
          // /operations/broadcasts to /users/broadcasts in YOJO-1762 so the
          // Users tab strip is shown. Kept behind CareTeamRoute (matches the
          // server-side `@auth(roles: ["careTeamMember"])` gate from YOJO-1727).
          { path: 'broadcasts', element: <CareTeamRoute><Broadcasts /></CareTeamRoute> },
          { path: 'broadcasts/new', element: <CareTeamRoute><BroadcastsNew /></CareTeamRoute> },
          { path: 'broadcasts/:broadcastId', element: <CareTeamRoute><BroadcastDetail /></CareTeamRoute> },
          { path: 'organizations', element: <Organizations /> },
          { path: 'yojo-team', element: <YojoTeam /> },
          // YOJO-1766: v1 WellbeingHub is deprecated. Every legacy
          // /users/:userId/wellbeing and /users/:userId/wellbeing/* path
          // redirects to its /participant-hub/* equivalent (query string
          // preserved; ai-chat lands on Care Plan). The v1 components remain
          // on disk only as the redirect source.
          {
            path: ':userId/wellbeing',
            element: <NurseOrAdminRoute><WellbeingRedirect /></NurseOrAdminRoute>,
          },
          {
            path: ':userId/wellbeing/*',
            element: <NurseOrAdminRoute><WellbeingRedirect /></NurseOrAdminRoute>,
          },
          {
            path: ':userId/participant-hub',
            element: <NurseOrAdminRoute><ParticipantHub /></NurseOrAdminRoute>,
            children: [
              {
                index: true,
                element: <Navigate to="overview" replace />
              },
              {
                path: 'overview',
                element: <OverviewTab />
              },
              {
                path: 'health',
                element: <HealthDataTab />
              },
              {
                path: 'sleep',
                element: <SleepTab />
              },
              {
                path: 'care-plan',
                element: <CarePlanTab />
              },
              {
                path: 'consults',
                element: <ConsultsTab />
              },
              {
                // YOJO-3260 [a coach creates a member's personalised Stone mode]
                path: 'stone-mode',
                element: <StoneModeTab />
              },
              {
                path: 'profile',
                element: <ProfileHistoryTab />
              }
            ]
          },
          // YOJO-3039 [/ws/chat unauthenticated]: the AI chat page is removed.
          // An old bookmark redirects to the participant's Care Plan (rather
          // than the catch-all "not found" page below).
          {
            path: ':userId/insights-chat',
            element: <NurseOrAdminRoute><WellbeingRedirect /></NurseOrAdminRoute>,
          }
        ]
      },
      {
        // YOJO-3371 [care-portal CRM page for partner enquiries]: CRM → Partner Enquiries is
        // for sales + operations + admin; every other CRM page stays admin/operations-only.
        path: 'crm',
        element: <RoleRoute roles={CRM_ROLES}><CRM /></RoleRoute>,
        children: [
          { index: true, element: <CrmIndexRedirect /> },
          { path: 'partner-enquiries', element: <CRMPartnerEnquiries /> },
          {
            element: <AdminOrOperationsRoute><Outlet /></AdminOrOperationsRoute>,
            children: [
              { path: 'dashboard', element: <CRMDashboard /> },
              { path: 'customers', element: <Customers /> },
              { path: 'customers/:customerId', element: <CustomerDetail /> },
              { path: 'shipments', element: <CRMShipments /> },
              { path: 'sources', element: <CRMSources /> },
              { path: 'carriers', element: <CRMCarriers /> },
              { path: 'service-cases', element: <CRMServiceCases /> },
              { path: 'waitlist', element: <CRMWaitlist /> },
              { path: 'newsletter', element: <CRMNewsletter /> },
              { path: 'payment-links', element: <PaymentLinks /> },
              { path: 'reports', element: <Reports /> },
            ]
          },
        ]
      },
      { path: 'programs', element: <ContentOrAdminRoute><Programs /></ContentOrAdminRoute>, children: [
        { index: true, element: <Navigate to="/programs/paths" replace /> },
        { path: 'paths', element: <LearningPaths /> },
        { path: 'paths/create', element: <LearningPaths /> },
        { path: 'paths/edit/:id', element: <LearningPaths /> },
        { path: 'journeys', element: <LearningJourneys /> },
        { path: 'journeys/create', element: <LearningJourneys /> },
        { path: 'journeys/edit/:id', element: <LearningJourneys /> }
      ] },
      {
        path: 'tools',
        element: <Tools />,
        children: [
          { index: true, element: <Navigate to="/tools/content-sync" replace /> },
          { path: 'notifications', element: <AdminOrDeveloperRoute><PushNotifications /></AdminOrDeveloperRoute> },
          {
            // YOJO-3383 [mobile-versions role guard]: same roles as its Tools tab (Tools.tsx); before this any signed-in user could open it by URL.
            path: 'mobile-versions',
            element: (
              <RoleRoute roles={[Role.Admin, Role.Developer, Role.Content]}>
                <MobileVersions />
              </RoleRoute>
            ),
            children: [
              { index: true, element: <Navigate to="/tools/mobile-versions/adoption" replace /> },
              { path: 'adoption', element: <MobileAdoption /> },
              { path: 'versions', element: <MobileVersionsList /> },
              { path: 'cache-policies', element: <MobileCachePolicies /> },
            ],
          },
          {
            // YOJO-2787 [Stone firmware management]: every firmware-api query is server
            // role-gated to developer/operations/admin, so the whole section is behind RoleRoute.
            path: 'firmware',
            element: (
              <RoleRoute roles={[Role.Admin, Role.Developer, Role.Operations]}>
                <Firmware />
              </RoleRoute>
            ),
            children: [
              { index: true, element: <Navigate to="/tools/firmware/releases" replace /> },
              { path: 'releases', element: <FirmwareReleases /> },
              { path: 'adoption', element: <FirmwareAdoption /> },
              { path: 'devices', element: <FirmwareDevices /> },
              { path: 'cohorts', element: <FirmwareCohorts /> },
            ],
          },
          { path: 'user-lookup', element: <AdminOrDeveloperRoute><UserLookup /></AdminOrDeveloperRoute> },
          { path: 'content-sync', element: <RoleRoute roles={[Role.Admin, Role.Developer, Role.Content, Role.Operations]}><ContentSync /></RoleRoute> },
          {
            path: 'stone-modes',
            element: (
              <RoleRoute roles={STONE_MODE_READ_ROLES}>
                <StoneModes />
              </RoleRoute>
            ),
          },
          {
            path: 'stone-modes/:modeId',
            element: (
              <RoleRoute roles={STONE_MODE_READ_ROLES}>
                <StoneModeDetailPage />
              </RoleRoute>
            ),
          },
        ]
      },
      {
        path: 'operations',
        element: <AdminOrOperationsRoute><Operations /></AdminOrOperationsRoute>,
        children: [
          { index: true, element: <Navigate to="/operations/segments" replace /> },
          { path: 'segments', element: <Segments /> },
          // YOJO-2555 [manual grant admin]. Deliberately NOT under /settings/capabilities: tier
          // authoring is admin-only, while looking up (and comping) one member's capabilities is
          // support's daily work. The Operations shell above is already AdminOrOperationsRoute; the
          // inner gate is belt-and-braces so the route cannot widen by being re-parented.
          { path: 'member-grants', element: <AdminOrOperationsRoute><MemberGrants /></AdminOrOperationsRoute> },
          // YOJO-3312 [ops set the Stone's offline count] — the offline count is ops' day-to-day dial; the
          // cap and the record window are admin-only inside the page. Same belt-and-braces inner gate.
          { path: 'stone-device-settings', element: <AdminOrOperationsRoute><StoneDeviceSettings /></AdminOrOperationsRoute> },
          { path: 'stripe-checkout', element: <StripeCheckoutTest /> }
        ]
      },
      {
        path: 'profile',
        element: <Profile />,
        children: [
          { index: true, element: <Navigate to="/profile/info" replace /> },
          { path: 'info', element: <PersonalInfo /> },
          { path: 'api-keys', element: <ApiKeys /> }
        ]
      },
      {
        path: 'settings',
        element: <AdminRoute><Settings /></AdminRoute>,
        children: [
          { index: true, element: <Navigate to="/settings/permissions" replace /> },
          { path: 'permissions', element: <PermissionGraph /> },
          // YOJO-3088 [Domain events page removed]: an old bookmark lands on the Permission
          // Viewer (rather than the catch-all "not found" page below).
          { path: 'events', element: <Navigate to="/settings/permissions" replace /> },
          { path: 'replay', element: <Replay /> },
          { path: 'dlq', element: <DeadLetterQueue /> },
          { path: 'logback', element: <LogbackConfig /> },
          { path: 'partners', element: <Partners /> },
          { path: 'service-keys', element: <ServiceApiKeys /> },
          // Tier authoring is admin-only (ADR-0007). The Settings shell above is already an
          // AdminRoute; the inner gate is deliberate belt-and-braces so the route cannot become
          // reachable by being re-parented under a laxer shell.
          { path: 'capabilities', element: <AdminRoute><Capabilities /></AdminRoute> }
        ]
      },
      // YOJO-3434 [care-portal: any address the app does not know blanks the whole screen, no catch-all route]:
      // any address no route above knows shows "not found" inside the shell. React Router ranks every full
      // branch, and a parent only matches when one of its children (or itself) matches the WHOLE address, so
      // this one splat also catches unknown nested addresses (/operations/subscriptions, /dashboard/nope,
      // /users/:id/participant-hub/nope). A signed-out visitor never gets here: ProtectedRoute above sends
      // them to /login first, like any other protected address.
      { path: '*', element: <NotFound /> },
    ],
  },
];
